import {
  formatPermissionLabel,
  type MenuPageJson,
  type PermissionGrantJson,
  type RoleMenuJson,
} from "../../setupWizard/utils/roleMenu";

/** Separator between the levels of a permission's label ("Roles › View"). */
export const PERMISSION_LABEL_SEPARATOR = " › ";

/**
 * Display names for permission catalogue keys, read from the catalogue menu
 * (GET /api/permission-catalogue/, via useGetPermissionCatalogue): a page is
 * named by its path of page names, an action by its page's path and the part
 * of its tree_key after the page's key, the way the roles editor labels it
 * (see actionTitle). A key the catalogue does not name is left out; callers
 * fall back to the raw key, so a permission removed from the catalogue still
 * shows in a role's history.
 */
export function permissionKeyLabels(menu: RoleMenuJson | null | undefined): Map<string, string> {
  const labels = new Map<string, string>();

  const visit = (page: MenuPageJson, trail: string[]) => {
    const path = [...trail, formatPermissionLabel(page.name || page.tree_key)];
    const pageLabel = path.join(PERMISSION_LABEL_SEPARATOR);
    if (page.tree_key) labels.set(page.tree_key, pageLabel);

    (page.actions ?? []).forEach((action) => {
      const key = action.tree_key;
      if (!key) return;
      const prefix = `${page.tree_key}-`;
      const suffix = page.tree_key && key.startsWith(prefix) ? key.slice(prefix.length) : key;
      const actionLabel = formatPermissionLabel(suffix) || formatPermissionLabel(action.name);
      labels.set(key, `${pageLabel}${PERMISSION_LABEL_SEPARATOR}${actionLabel}`);
    });

    (page.sub_pages ?? []).forEach((sub) => visit(sub, path));
  };

  (menu?.pages ?? []).forEach((page) => visit(page, []));
  return labels;
}

/** The words a grant is described with; the caller supplies them translated. */
export interface GrantWords {
  undecided: string;
  online: string;
  offline: string;
  on: string;
  off: string;
  locked: string;
}

/**
 * One side of a change as text: "Online on, Offline off" (", locked" when the
 * editor locked it), or the undecided word when there was no decision.
 */
export function describeGrant(grant: PermissionGrantJson | null, words: GrantWords): string {
  if (!grant) return words.undecided;
  const parts = [
    `${words.online} ${grant.active ? words.on : words.off}`,
    `${words.offline} ${grant.offline ? words.on : words.off}`,
  ];
  if (grant.locked) parts.push(words.locked);
  return parts.join(", ");
}
